import React, { Fragment } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import Button from "../Button";
import { withTranslation } from "../../i18n";
import useStyles from "./styles";
import link from "../../static/text/link";
import { encryptString } from "../../helpers/encryption";
import { showErrorAlert } from "../../helpers/general";
import { trackEventTagManager } from "../../helpers/utils/tag-manager";
import { shouldForceXtLane, isSinglePurchasePackage } from "../../helpers/xtLaneFlag";
import { sanitizePagePrice } from "../../helpers/pagePrice";
import {
  calculateCleengDiscountPercentage,
  formatCleengDiscountPercentage,
  formatCleengMonthlyPrice as formatCleengMonthlyPriceValue,
  getCleengCurrentPriceText,
  getCleengPackageExclTaxPriceText,
  getCleengRetailPriceText,
} from "../../helpers/cleengPricing";
import { buildCleengCheckoutQuery } from "../../helpers/cleengCheckoutLink";
import {
  appendCheckoutIdToUrl,
  createCheckoutId,
} from "../../helpers/ads/conversion-tracking";

const checklistIcon = require("../../static/images/mfs/checklist.png");

function PlanPrice(props) {
  const {
    t,
    rootStyle,
    item,
    ctnStyle,
    index,
    i18n,
    referralId,
    promoCode,
    promo_price,
    pagePrice: rawPagePrice,
    page_source,
    ctnPromoStyle,
    ctnMainContentStyle,
    btnAbsoluteStyle,
    bottomInnerStyle,
    bogoEnabled: bogoEnabledProp,
    figmaPricingTypography,
  } = props;
  // Trello 546 C008: only a same-site path may become the card CTA href.
  const pagePrice = sanitizePagePrice(rawPagePrice);
  const classes = useStyles();
  const currentLanguage = i18n.language;
  const validityPeriodAmount = Number(item && item.validity_period_amount);
  const cleengPrice = item && item.cleeng_price && item.cleeng_price.source === "cleeng"
    ? item.cleeng_price
    : null;
  const bogoEnabled = !!bogoEnabledProp
    || !!(item && item.promotion_text && /get\s*2|dapat\s*2/i.test(item.promotion_text));
  const normalizedPageSource = String(page_source || "").replace(/^\/+/, "");
  const normalizeRedeemFullDiscountLabel = (text) => {
    const normalizedText = String(text || "").trim();
    if (!normalizedText) return null;

    const withoutBadgePrefix = normalizedText
      .replace(/^(popular|populer|best value|harga terbaik)\s*-\s*/i, "")
      .trim();

    return /(?:diskon\s*)?100\s*%|save\s*100\s*%|100\s*%\s*off/i.test(withoutBadgePrefix)
      ? withoutBadgePrefix
      : null;
  };
  const getRedeemFullDiscountLabel = () => (
    normalizeRedeemFullDiscountLabel(item.discount_label)
    || normalizeRedeemFullDiscountLabel(item.discount_caption)
  );
  const isRedeemBogoSummary = (normalizedPageSource === "redeem_buy_1_get_1" || normalizedPageSource === "redeem")
    && (bogoEnabled || !!getRedeemFullDiscountLabel());

  const formatCleengMonthlyPrice = (divideByBogo = bogoEnabled) => {
    if (!cleengPrice) return null;
    const months = Number(item && item.validity_period_amount) || 1;
    return formatCleengMonthlyPriceValue(cleengPrice, {
      months,
      divideByBogo,
    });
  };

  const cleengDiscountPercentage = calculateCleengDiscountPercentage(cleengPrice);
  const getCleengSummaryBadgeMeta = () => {
    if (!cleengPrice) {
      return null;
    }

    if (isRedeemBogoSummary) {
      const redeemFullDiscountLabel = getRedeemFullDiscountLabel();
      if (redeemFullDiscountLabel) {
        return {
          price_attribute: "",
          discount_label: redeemFullDiscountLabel,
          discount_caption: redeemFullDiscountLabel,
        };
      }
    }

    const discountPct = formatCleengDiscountPercentage(cleengDiscountPercentage);
    const hasDiscount = Number.isFinite(discountPct) && discountPct > 0;
    const isBestValue = item.is_best_value
      || validityPeriodAmount === 6
      || /best value|harga terbaik/i.test(String(item.price_attribute || ""));
    const isBestDeal = item.is_best_deal || isBestValue || validityPeriodAmount >= 3;
    const discountLabel = currentLanguage === "id" ? `Diskon ${discountPct}%` : `${discountPct}% Off`;
    const bestValueLabel = currentLanguage === "id" ? "HARGA TERBAIK" : "BEST VALUE";
    const popularFallbackLabel = currentLanguage === "id" ? "POPULER" : "POPULAR";
    const popularLabel = isBestValue
      ? (/best value|harga terbaik/i.test(String(item.price_attribute || "")) ? item.price_attribute : bestValueLabel)
      : (item.price_attribute || popularFallbackLabel);
    const flexibleLabel = currentLanguage === "id" ? "FLEKSIBEL" : "FLEXIBLE";

    if (!hasDiscount) {
      return { price_attribute: "", discount_label: "", discount_caption: flexibleLabel };
    }

    if (isBestDeal) {
      return {
        price_attribute: popularLabel,
        discount_label: discountLabel,
        discount_caption: `${popularLabel} - ${discountLabel.toUpperCase()}`,
      };
    }

    return {
      price_attribute: "",
      discount_label: discountLabel,
      discount_caption: discountLabel.toUpperCase(),
    };
  };
  const summaryBadgeMeta = getCleengSummaryBadgeMeta();

  function getObjectName() {
    switch (page_source) {
      case "promo/subs3":
      case "promo":
      case "quiz":
      case "plan":
      case "/promo3a":
      case "/deals":
        return "monthly_price_text";
      case "promo/subs4":
        return "weekly_price_text";
      default:
        return "daily_price_text";
    }
  }

  function getSinglePeriodDiscountPriceText() {
    if (cleengPrice || !item || !item.pricing || validityPeriodAmount !== 1) {
      return null;
    }

    const pricingName = getObjectName();
    const pricingValue = item.pricing[pricingName];
    if (pricingValue && typeof pricingValue === "object") {
      const promoText = pricingValue.promo || null;
      const normalText = pricingValue.normal || null;

      if (promoText && promoText !== normalText) {
        return promoText;
      }
    }

    const fallbackDiscountPrice = item.pricing.discounted_price_text || item.pricing.main_price_text || null;
    if (fallbackDiscountPrice && fallbackDiscountPrice !== item.price_text) {
      return fallbackDiscountPrice;
    }

    return null;
  }

  function renderHref() {
    const encryptId = encryptString(item.id);
    let hrefString = pagePrice
      ? `${pagePrice}?id=${encryptId}`
      : `${link.mobile.login(encryptId)}?state=register`;
    // let hrefString = `${link.mobile.login(item.id)}?state=register`;
    if (referralId) {
      hrefString += `&referral_id=${referralId}`;
    } else if (item.promo) {
      hrefString += `&active_promo=${item.promo.code}`;
    }
    if (promo_price) {
      hrefString += "&promo_price=true";
    }
    const cleengQuery = buildCleengCheckoutQuery({
      cleengPrice,
      item,
      pageSource: page_source,
      bogoEnabled,
      language: currentLanguage,
    });
    if (cleengQuery) {
      hrefString += `&${cleengQuery}`;
    }
    // Trello 546 Phase 2: layout/landing-v2/Plan.js's desktop renderPlanPrice()
    // passes its own (sometimes URL-sourced, see plan-v3/plan-v4) page_price
    // straight into this component as `pagePrice`, which the branch above
    // uses verbatim — force xt lane here too so the invariant doesn't depend
    // on which card-select href wins. item is already resolved here, so skip
    // forcing for a SINGLE_PURCHASE/one-time pick (owner decision: stays on
    // the legacy path this round).
    if (shouldForceXtLane() && !isSinglePurchasePackage(item)) {
      hrefString += "&xt=1";
    }
    return hrefString;
  }

  function getTranslate(text) {
    switch (text) {
      case "Popular":
        if (currentLanguage === "id") {
          return "Populer";
        }
        return "Popular";
      case "Best Value":
        if (currentLanguage === "id") {
          return "Harga Terbaik";
        }
        return "Best Value";
      case "":
        return "Basic Plan";
      case "close":
        if (currentLanguage === "id") {
          return "Tutup";
        }
        return "Close";
      default:
        return text;
    }
  }

  const handleRedirect = () => {
    if (
      item
      && item.promo
      && item.promo.used
      && item.promo.used.status === true
    ) {
      showErrorAlert(
        null,
        item.promo.used.message.used,
        () => { },
        getTranslate("close")
      );
    } else {
      const checkoutId = createCheckoutId();
      window.location.href = appendCheckoutIdToUrl(renderHref(), checkoutId);

      trackEventTagManager(
        `select-package:${page_source}`,
        "Click: Select-Package",
        "Click",
        { name: item.name, pricing: item.pricing },
        page_source
      );
    }
  };

  function renderSubPrice() {
    const renderFigmaSubPrice = (text) => {
      if (!figmaPricingTypography || !text || String(text).indexOf(" / ") === -1) {
        return <span className={classes.mainPrice}>{text}</span>;
      }
      const parts = String(text).split(" / ");
      return (
        <span className={classes.mainPrice}>
          <span className={classes.figmaPriceAmount}>{parts[0]}</span>
          <span className={classes.figmaPriceSuffix}>{` / ${parts.slice(1).join(" / ")}`}</span>
        </span>
      );
    };

    if (cleengPrice) {
      if (page_source === "plan") {
        const monthlyPrice = formatCleengMonthlyPrice(bogoEnabled);
        if (monthlyPrice) {
          const subText = bogoEnabled
            ? (currentLanguage === "id"
              ? `${monthlyPrice} / Orang / Bulan`
              : `${monthlyPrice} / Person / Month`)
            : (currentLanguage === "id"
              ? `${monthlyPrice} / Bulan`
              : `${monthlyPrice} / Month`);
          return renderFigmaSubPrice(subText);
        }
      }
      const subText = cleengPrice.sub_price_text || cleengPrice.period_label_text;
      if (subText) {
        return renderFigmaSubPrice(subText);
      }
      return null;
    }
    if (item.pricing && item.pricing.sub_price_text) {
      return renderFigmaSubPrice(item.pricing.sub_price_text);
    }
    return null;
  }

  function renderPromoCode() {
    if (item.promo && item.promo.applied_text) {
      return (
        <div className={classes.ctnPromo}>
          <div id="ctn-check">
            <img src={checklistIcon} alt="check" />
          </div>
          <span>{item.promo.applied_text}</span>
        </div>
      );
    }
    return null;
  }

  function renderTextTile() {
    if (summaryBadgeMeta) {
      if (summaryBadgeMeta.price_attribute) {
        return (
          <Fragment>
            <span>{summaryBadgeMeta.price_attribute}</span>
            {!!summaryBadgeMeta.discount_label && (
              <span id="activeText">{`- ${summaryBadgeMeta.discount_label}`}</span>
            )}
          </Fragment>
        );
      }
      if (summaryBadgeMeta.discount_label) {
        return <span id="activeText">{summaryBadgeMeta.discount_label}</span>;
      }
      return <span>{summaryBadgeMeta.discount_caption}</span>;
    }
    if (isRedeemBogoSummary) {
      const redeemFullDiscountLabel = getRedeemFullDiscountLabel();
      if (redeemFullDiscountLabel) {
        return <span id="activeText">{redeemFullDiscountLabel}</span>;
      }
    }
    if (item.price_attribute) {
      return (
        <Fragment>
          <span>{`${item.price_attribute} -`}</span>
          <span id="activeText">{`${item.discount_label}`}</span>
        </Fragment>
      );
    }
    if (!item.price_attribute && item.discount_label) {
      return <span id="activeText">{`${item.discount_label}`}</span>;
    }
    return <span>{item.discount_caption}</span>;
  }

  const displayDiscountPriceText = item.disc_price_text || getSinglePeriodDiscountPriceText();
  const displayCleengCurrentPriceText = getCleengCurrentPriceText(cleengPrice);
  const displayCleengPackagePriceText = getCleengPackageExclTaxPriceText(cleengPrice) || displayCleengCurrentPriceText;
  const displayCleengRetailPriceText = getCleengRetailPriceText(
    cleengPrice,
    displayCleengCurrentPriceText,
  );

  return (
    <a onClick={handleRedirect} className={`${classes.aStyle} ${ctnStyle}`}>
      <div className={classes.baseRoot}>
        <div className={`${classes.root} ${rootStyle}`}>
          <div className={`${classes.bottomInner} ${bottomInnerStyle}`}>{renderTextTile()}</div>
          <div className={classes.itemInner}>
            <div
              className={`${classes.whiteContent} ${promoCode || (item.promo && item.promo.applied_text)
                ? classes.moreHeight
                : {}
                } ${ctnMainContentStyle}`}
            >
              <span className={classes.monthPrice}>{item.name}</span>
              {!!item.promotion_text && (
                <div className={ctnPromoStyle}>
                  <span className={classes.txtIntro}>{item.promotion_text}</span>
                </div>
              )}
              {renderPromoCode()}
              {cleengPrice ? (
                <span className={clsx(classes.oldPrice, classes.blackText)}>
                  {displayCleengRetailPriceText ? (
                    <>
                      <span id="slash-text">
                        {displayCleengRetailPriceText}
                      </span>
                      {" "}
                      <span>{displayCleengPackagePriceText}</span>
                    </>
                  ) : (
                    <span>{displayCleengPackagePriceText}</span>
                  )}
                </span>
              ) : displayDiscountPriceText ? (
                <span className={clsx(classes.oldPrice, classes.blackText)}>
                  <span id={displayDiscountPriceText && "slash-text"}>
                    {item.price_text}
                  </span>
                  {" "}
                  <span>{displayDiscountPriceText}</span>
                </span>
              ) : index !== 0 ? (
                <span
                  className={`${classes.oldPrice} ${classes.blackText} ${!displayDiscountPriceText ? classes.ovrBlackText : null
                    }`}
                >
                  <span>{item.price_text_period}</span>
                </span>
              ) : null}
              {renderSubPrice()}
              <div className={`${classes.btnAbsolute} ${btnAbsoluteStyle}`}>
                <Button styles={classes.btnStyle} gradient>
                  <span>{t("choose_plan")}</span>
                </Button>
              </div>
            </div>
          </div>
        </div>
        {/* {item.discount_caption ? (
          <div className={classes.btnAbsolute}>
            <Button styles={classes.btnStyle} gradient><span>{t('choose_plan')}</span></Button>
          </div>
        ) : null} */}
      </div>
    </a>
  );
}

PlanPrice.propTypes = {
  t: PropTypes.func.isRequired,
  i18n: PropTypes.object.isRequired,
  rootStyle: PropTypes.any,
  ctnStyle: PropTypes.any,
  item: PropTypes.any,
  promoCode: PropTypes.any,
};

PlanPrice.defaultProps = {
  rootStyle: null,
  ctnStyle: null,
  item: null,
  promoCode: null,
  promo_price: false,
};

PlanPrice.getInitialProps = async () => ({
  namespacesRequired: ["myfitsociety"],
});

export default withTranslation("myfitsociety")(PlanPrice);
